import { type DocumentNode, gql, useMutation, useQuery } from '@apollo/client';
import OfflineBoltIcon from '@mui/icons-material/OfflineBolt';
import Box from '@mui/material/Box/Box';
import FormLabel from '@mui/material/FormLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import type React from 'react';
import { useEffect, useState } from 'react';
import { API_PATH, HIRES_FORMAT_SLUG } from 'src/constants';
import { AssetNodeType, formatSet } from 'src/dynamoQueries/assets';
import { generatePresignedURL } from 'src/dynamoQueries/cloud';
import viewStateVar from 'src/store/viewState';
import AkomiDialog from './AkomiDialog';
import AkomiDialogActions from './AkomiDialogActions';
import { skeletonSx } from './DialogSkeleton';

interface LibraryDownloadDialogProps {
  isOpen: boolean;
}

interface FormatTypeNode {
  id: string;
  slug: string;
  name: string;
}

interface FormatNode {
  id: string;
  filename: string;
  online: boolean;
  presignable: boolean;
  fmtType: FormatTypeNode;
}

interface FormatEdge {
  node: FormatNode;
}

interface AssetPermissions {
  hires?: boolean;
}

interface AssetQueryResponse {
  asset: {
    myAssetPermissions: string;
    formatSet: {
      edges: FormatEdge[];
    };
  };
}

interface PresignedUrlResponse {
  generatePresignedUrl: {
    presignedUrl: string;
  };
}

interface PresignedUrlVariables {
  formatId: string;
}

const LibraryDownloadDialog: React.FC<LibraryDownloadDialogProps> = ({ isOpen }) => {
  const [selectedFormatId, setSelectedFormatId] = useState<string | null>(null);
  const [selectedFormat, setSelectedFormat] = useState<FormatEdge | null>(null);
  const [fSet, setFormatSet] = useState<FormatEdge[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const buildQuery = (): DocumentNode => {
    const asn = AssetNodeType(true);

    (asn as Record<string, unknown>).formatSet = formatSet(true);

    ((asn as Record<string, unknown>).formatSet as Record<string, unknown>).__args = { downloadable: true };
    const q = {
      query: {
        asset: {
          __args: {
            id: viewStateVar().libraryDownloadDialog as string,
          },
          ...asn,
        },
      },
    };
    const gQuery = jsonToGraphQLQuery(q, { pretty: true });
    return gql(gQuery);
  };

  const buildPresignedURLMutation = (): DocumentNode => {
    const m = generatePresignedURL(true);
    const mutation = jsonToGraphQLQuery(m, { pretty: true });
    return gql(mutation);
  };

  const [
    presignedURLMutation,
    { data: presignedUrlData, loading: generatingPresignedUrl, error: errorGeneratingPresignedUrl },
  ] = useMutation<PresignedUrlResponse, PresignedUrlVariables>(buildPresignedURLMutation());

  const { loading } = useQuery<AssetQueryResponse>(buildQuery(), {
    skip: !viewStateVar().libraryDownloadDialog,
    onCompleted: (data) => {
      const permissions: AssetPermissions = JSON.parse(JSON.parse(data.asset.myAssetPermissions));
      if (!permissions.hires) {
        setFormatSet(data.asset.formatSet.edges.filter((format) => format.node.fmtType.slug !== HIRES_FORMAT_SLUG));
      } else {
        setFormatSet(data.asset.formatSet.edges);
      }
    },
    onError: (e) => {
      console.log('Error loading formats:', e);
      setError(e.message);
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: onClose is a stable function defined in this component; only need to react to presignedUrlData changes
  useEffect(() => {
    if (presignedUrlData?.generatePresignedUrl?.presignedUrl) {
      // Create a temp anchor tag and click it
      const atag = document.createElement('a');
      atag.setAttribute('href', presignedUrlData.generatePresignedUrl.presignedUrl);
      atag.click();
      atag.remove();
      onClose();
    }
  }, [presignedUrlData]);

  const onClose = (): void => {
    viewStateVar({
      ...viewStateVar(),
      libraryDownloadDialog: null,
    });
  };

  const onFormatSelect = (e: SelectChangeEvent<string | null>): void => {
    const fnodeId = e.target.value as string;
    const fmt = fSet?.find((f) => f.node.id === fnodeId) ?? null;
    setError(null);
    setSelectedFormat(fmt);
    setSelectedFormatId(fnodeId);
  };

  const handleDownload = (): void => {
    if (!selectedFormatId) return;
    if (selectedFormat?.node.presignable) {
      presignedURLMutation({ variables: { formatId: selectedFormatId } });
    } else {
      const atag = document.createElement('a');
      atag.setAttribute('href', `${window.location.origin}${API_PATH}/download/format/${selectedFormatId}`);
      atag.click();
      atag.remove();
      onClose();
    }
  };

  const isLoading = loading || (!fSet && !error);
  const noFormats = !loading && fSet !== null && fSet.length === 0;

  return (
    <AkomiDialog
      open={isOpen}
      onClose={onClose}
      title="Download"
      maxWidth="xs"
      loading={isLoading}
      skeleton={
        <Box sx={{ mb: 2 }}>
          <Box sx={{ ...skeletonSx, width: 120, height: 12, mb: '6px' }} />
          <Box sx={{ ...skeletonSx, width: '100%', height: 36 }} />
        </Box>
      }
      actions={
        <AkomiDialogActions
          variant="cancel-confirm"
          primaryLabel="Download"
          onPrimary={handleDownload}
          onSecondary={onClose}
          primaryDisabled={
            !selectedFormatId || generatingPresignedUrl || !!presignedUrlData?.generatePresignedUrl?.presignedUrl
          }
          loading={isLoading}
        />
      }
    >
      {noFormats ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100px' }}>
          No downloadable formats available.
        </Box>
      ) : (
        <>
          <Box sx={{ mb: 2 }}>
            <FormLabel sx={{ display: 'block', fontSize: '13px', color: 'text.secondary', mb: '6px' }}>
              Select a file format
            </FormLabel>
            <Select fullWidth size="small" value={selectedFormatId ?? ''} onChange={onFormatSelect}>
              {(fSet ?? []).map((f) => (
                <MenuItem key={f.node.id} value={f.node.id} disabled={!f.node.online && !f.node.presignable}>
                  {!f.node.online && !f.node.presignable && <OfflineBoltIcon sx={{ pr: '7px' }} />}
                  {`${f.node.fmtType.name} (${f.node.filename})`}
                </MenuItem>
              ))}
            </Select>
          </Box>
          {(error || errorGeneratingPresignedUrl) && (
            <Typography color="error">{error || errorGeneratingPresignedUrl?.message || ''}</Typography>
          )}
        </>
      )}
    </AkomiDialog>
  );
};

export default LibraryDownloadDialog;
